import { CHEST_SOURCE_LABEL, chestLabel, streakRun } from '@/game/chest-label';
import { CHEST_SOURCES, type ChestSource } from '@/game/chests';
import type { ChestPose } from './chest-mesh';
import {
  BASE_PITCH,
  BASE_RIM,
  BASE_YAW,
  STILL_EMIT,
  STILL_LID,
  STILL_RIM,
  chestLayoutFor,
  idleYaw,
  type ChestLayout,
} from './chest-pose';
import { clamp01, easeInCubic, easeOutBack, easeOutCubic, spring } from './chest-timeline';

/**
 * The chest ARRIVAL — "you've earned a chest" — as pure functions of one
 * clock. Nothing here keeps a timer: the stage (screens/ChestScreen.tsx)
 * steps `ArrivalClock` and every element (the veil, the beams, the chests,
 * the pedestals, the rings, the title, the buttons, the LATER flight, the
 * dock pill) is a read of `arrivalFrame(plan, t, decision, view)` — a
 * capture that steps the clock films exactly what a player sees, and no
 * element can run ahead of another (critique rounds 54–57).
 *
 *   0.00  the veil rises in the chest's starting-rarity colour
 *   0.16  a beam of light drops onto the first pedestal (each chest has its own, 0.30 apart)
 *   0.50  the chest falls through its beam and SLAMS onto the lit pedestal
 *         (dust ring + shockwave ring; nothing else moves — no screen shake)
 *   0.86  the source label slams in as the title ("RANK-UP CHEST")
 *   1.34  TAP TO OPEN and LATER come up; then it idles, alive: a breathing bob,
 *         the rarity's light leaking from the lid seam, drifting sparkles, and
 *         a small lid rattle every RATTLE_EVERY_S
 *
 * Two decisions end it: `open` (TAP TO OPEN — the reveal takes the chest
 * over on the same clock) and `later` (each chest shrinks and flies an arc
 * into the top-right chest pill, which bursts and ticks up).
 */

/**
 * The title per source: a LABEL, never explanatory sub-text (the owner has had sub-text removed twice).
 * The words (and a weekly chest's "2ND PLACE \u00b7 WEEKLY $CHAIN", a streak chest's "5 WINS IN A ROW") are the shared
 * `chestLabel` (src/game/chest-label.ts), the same ones the chest screen, the notification and the Rewards history use.
 */
export const ARRIVAL_LABEL = Object.fromEntries(
  CHEST_SOURCES.map((s) => [s, CHEST_SOURCE_LABEL[s].caps]),
) as Record<ChestSource, string>;

export { chestLabel, streakRun };

/** A pedestal's radius in chest widths: one chest's, or side by side (they keep a gap at PITCH). */
export const pedestalRadius = (n: number): number => (n > 1 ? 0.6 : 0.66);

/** The title for a batch: its chests' one label, or — labels mixed — a count of new chests (each chest then carries its own label). */
export function arrivalTitle(
  chests: readonly { source: ChestSource; sourceRef?: string | undefined }[],
): string {
  const labels = chests.map((c) => chestLabel(c));
  const first = labels[0] ?? ARRIVAL_LABEL.rank_up;
  return labels.every((l) => l === first) ? first : 'NEW CHESTS';
}

/**
 * A label's size class: a long one shrinks to stay on one line on a phone (the arrival title and the chest kicker).
 * "2ND PLACE \u00b7 WEEKLY $CHAIN" is 25 characters, "3RD PLACE \u00b7 WEEKLY WIN STREAKS" 30 and "3RD PLACE \u00b7 WEEKLY CARD STREAKS" 31.
 */
export const labelSize = (label: string): '' | 'long' | 'longer' | 'longest' =>
  label.length > 26 ? 'longest' : label.length > 22 ? 'longer' : label.length > 14 ? 'long' : '';

/** The arrival title's size class, as a className suffix. */
export const titleSize = (title: string): string => {
  const size = labelSize(title);
  return size ? ` ${size}` : '';
};

/** At most this many chests land side by side (a bigger batch still counts all of them in "×N"). */
export const MAX_SHOWN = 3;

/** Seconds on the arrival clock. */
export const A = {
  /** The veil's rise. */
  veilIn: [0, 0.42],
  /** Chest i's beam starts dropping at beam + i·gap and takes beamDrop to reach the pedestal. */
  beam: 0.16,
  beamDrop: 0.3,
  /** Chest i starts to fall at fall + i·gap; the fall takes fallS (ease-in: it gathers weight). */
  fall: 0.5,
  fallS: 0.36,
  gap: 0.3,
  /** The landing: dust ring and shockwave ring. */
  dustS: 0.62,
  shockS: 0.5,
  /** The title slams in this long after the first landing, over slamS. */
  titleAfter: 0.02,
  slamS: 0.32,
  /** "×N" after the last landing. */
  countAfter: 0.12,
  /** Buttons after the last landing, over buttonsS. */
  buttonsAfter: 0.5,
  buttonsS: 0.3,
  /** Idle begins (the landing spring has settled). */
  idleAfter: 0.62,
  /** The lid rattles every RATTLE_EVERY_S (the first a beat into the idle), each for rattleS. */
  rattleFirst: 1.0,
  rattleEvery: 2.5,
  rattleS: 0.34,
  /** The words and buttons leave on a decision. */
  outS: 0.08,
  /** TAP TO OPEN: the veil floods into the reveal's ground; the other chests leave. */
  floodS: 0.45,
  glideS: 0.36,
  exitS: 0.34,
  /** LATER: per chest a wind-up, then the flight; chests leave stowGap apart. */
  stowWind: 0.1,
  stowFlight: 0.5,
  stowGap: 0.12,
  /** The veil lifts off the page once LATER's last chest is in the pill. */
  veilOutS: 0.4,
  /** After the last chest reaches the pill, the dock holds, then the layer is gone. */
  stowHold: 0.55,
  dockOut: 0.3,
} as const;

export const RATTLE_EVERY_S = A.rattleEvery;
export const RATTLE_S = A.rattleS;

export interface ArrivalPlan {
  /** Chests in the batch (all of them count); the first MAX_SHOWN land. */
  count: number;
  /** Reduced motion: the end state with a fade (the stage fades the layer in). */
  reduced: boolean;
}

export interface ArrivalDecision {
  openAt: number | null;
  laterAt: number | null;
}

export interface ArrivalView {
  width: number;
  height: number;
  /** The chest pill's centre (the LATER flight's target), px in the layer. */
  pill: { x: number; y: number };
  /** The pill's icon size, px (the flight ends that big). */
  pillIcon: number;
}

export const shown = (plan: ArrivalPlan): number => Math.max(1, Math.min(MAX_SHOWN, plan.count));

export const beamAt = (i: number): number => A.beam + i * A.gap;
export const fallAt = (i: number): number => A.fall + i * A.gap;
export const landAt = (i: number): number => fallAt(i) + A.fallS;
export const titleAt = (): number => landAt(0) + A.titleAfter;
export const countAt = (plan: ArrivalPlan): number => landAt(shown(plan) - 1) + A.countAfter;
export const buttonsAt = (plan: ArrivalPlan): number => landAt(shown(plan) - 1) + A.buttonsAfter;
export const idleAt = (plan: ArrivalPlan): number => landAt(shown(plan) - 1) + A.idleAfter;
/** Rattle k (0, 1, …) and the chest it shakes. */
export const rattleAt = (plan: ArrivalPlan, k: number): number =>
  idleAt(plan) + A.rattleFirst + k * A.rattleEvery;
export const rattleChest = (plan: ArrivalPlan, k: number): number => k % shown(plan);
/** LATER: chest i's wind-up starts, its flight starts, it reaches the pill. */
export const stowStart = (laterAt: number, i: number): number => laterAt + i * A.stowGap;
export const flightStart = (laterAt: number, i: number): number =>
  stowStart(laterAt, i) + A.stowWind;
export const stowLand = (laterAt: number, i: number): number =>
  flightStart(laterAt, i) + A.stowFlight;
/** The layer is gone. */
export function doneAt(plan: ArrivalPlan, dec: ArrivalDecision): number | null {
  if (dec.laterAt !== null) return stowLand(dec.laterAt, shown(plan) - 1) + A.stowHold + A.dockOut;
  return null;
}
/** When the reduced-motion arrival starts its clock: the end state. */
export const reducedStart = (plan: ArrivalPlan): number => idleAt(plan);

/**
 * Where each landed chest stands: one chest exactly where the reveal puts it
 * (so TAP TO OPEN hands it over in place); two or three side by side on the
 * same line, smaller, never closer than 16 px to a phone's edges.
 */
export function arrivalSlots(n: number, width: number, height: number): ChestLayout[] {
  const L = chestLayoutFor(width, height);
  const k = Math.max(1, Math.min(MAX_SHOWN, n));
  if (k === 1) return [L];
  const pitch = 1.45; // centre-to-centre, in chest widths (the pedestals never touch)
  const want = L.size * (k === 2 ? 0.74 : 0.6);
  // A turned chest (and its pedestal) reads ~1.4 widths across: keep that 16 px off each edge.
  const fit = (width - 32) / (pitch * (k - 1) + 1.4);
  const size = Math.min(want, fit);
  const y = L.y + L.size * 0.08;
  return Array.from({ length: k }, (_, i) => ({
    ...L,
    x: L.x + (i - (k - 1) / 2) * size * pitch,
    y,
    size,
  }));
}

export interface ArrivalChestFrame {
  /** Whether it is drawn at all (not before its fall, not once the flyer has it). */
  visible: boolean;
  /** Canvas chest alpha (the other chests leave on TAP TO OPEN). */
  alpha: number;
  pose: ChestPose;
  /** Its slot (the pedestal's place, the beam's line). */
  slot: ChestLayout;
  /** 0..1: the pedestal's light (on with the beam, flares at the landing). */
  pedestal: number;
  /** The beam: how far down its foot has come (0..1) and its strength. */
  beamFoot: number;
  beamK: number;
  /** 0..1 since it landed (its label comes in with it). */
  landed: number;
  /** The pedestal taking the hit: 0..1 pressed down (springs back). */
  press: number;
  /** Landing rings, 0..1 through each, or -1. */
  dust: number;
  shock: number;
  /** Light out of the lid seam, 0..1+. */
  seam: number;
  /** LATER's flight (DOM, over the top bar): null when not flying. */
  flight: null | {
    u: number;
    x: number;
    y: number;
    size: number;
    /** The wind-up's stretch, relaxing over the first of the flight (y scale). */
    stretch: number;
    /** The streak of light behind it runs back to here; its strength. */
    tail: { x: number; y: number; size: number };
    glow: number;
  };
}

export interface ArrivalFrame {
  t: number;
  /** The veil's strength over the page (0..1). */
  veil: number;
  /** TAP TO OPEN: the veil flooding into the reveal's full ground (0..1). */
  flood: number;
  /** Title: slam progress 0..1 and its leave 0..1. */
  title: number;
  titleOut: number;
  count: number;
  countOut: number;
  buttons: number;
  buttonsOut: number;
  /** Buttons take input. */
  live: boolean;
  /** The idle has begun (the hint pulses, sparkles drift). */
  idle: boolean;
  chests: ArrivalChestFrame[];
  /** The dock pill (when the shell's pill is not on screen): 0..1 in, and its leave. */
  dock: number;
  /** The layer's own alpha (reduced motion fades it in; LATER's last beat fades it out). */
  layer: number;
  done: boolean;
}

/** Quadratic Bézier. */
function bez(p0: number, p1: number, p2: number, u: number): number {
  const v = 1 - u;
  return v * v * p0 + 2 * v * u * p1 + u * u * p2;
}

/** The flight's path parameter at flight progress u (slow off the pedestal, sucked into the pill). */
export function flightEase(u: number): number {
  // Quick off the pedestal, gathering speed into the pill.
  const x = clamp01(u);
  return x * (0.35 + 0.65 * x);
}

/** The flight's head at path parameter s: from the slot up and over into the pill. */
export function flightPoint(
  slot: ChestLayout,
  view: ArrivalView,
  u: number,
): { x: number; y: number; size: number } {
  const p0 = { x: slot.x, y: slot.y - slot.size * 0.12 };
  const p2 = view.pill;
  // A clear arc: up off the pedestal and over, coming into the pill from the left at its height.
  // Up and across, then rising INTO the pill from below — never across the balance beside it.
  const p1 = { x: p2.x - view.width * 0.04, y: p2.y + (p0.y - p2.y) * 0.42 };
  const s = flightEase(u);
  // It shrinks at once (to FLIGHT_SHRINK of itself in the first quarter), then into the pill's icon.
  const x = clamp01(u);
  const mid = slot.size * FLIGHT_SHRINK;
  const size =
    x < 0.25
      ? slot.size + (mid - slot.size) * easeOutCubic(x / 0.25)
      : mid + (view.pillIcon - mid) * easeInCubic((x - 0.25) / 0.75);
  return { x: bez(p0.x, p1.x, p2.x, s), y: bez(p0.y, p1.y, p2.y, s), size };
}

/** The flight's size after its first quarter, as a fraction of the landed chest. */
export const FLIGHT_SHRINK = 0.34;

/** A small deterministic hash in 0..1 (per chest and rattle: no Math.random on the clock). */
const h01 = (a: number, b = 0): number => {
  const x = Math.sin(a * 127.1 + b * 311.7) * 43758.5453;
  return x - Math.floor(x);
};

/**
 * Everything the arrival shows at time t. Pure: the same (plan, t,
 * decision, view) is the same frame.
 */
export function arrivalFrame(
  plan: ArrivalPlan,
  t: number,
  dec: ArrivalDecision,
  view: ArrivalView,
): ArrivalFrame {
  const n = shown(plan);
  const slots = arrivalSlots(n, view.width, view.height);
  const decidedAt = dec.openAt ?? dec.laterAt;
  const out = decidedAt === null ? 0 : clamp01((t - decidedAt) / A.outS);
  const live = decidedAt === null && t >= buttonsAt(plan);
  const idle = t >= idleAt(plan);

  // The veil: up with the arrival, lifted off the page under LATER, a flood on TAP TO OPEN.
  let veil = easeOutCubic(clamp01((t - A.veilIn[0]) / (A.veilIn[1] - A.veilIn[0])));
  if (dec.laterAt !== null) {
    // The veil holds until the last chest is in the pill, then lifts off the page.
    const from = stowLand(dec.laterAt, n - 1) - 0.05;
    veil *= 1 - easeOutCubic(clamp01((t - from) / A.veilOutS));
  }
  const flood = dec.openAt === null ? 0 : easeOutCubic(clamp01((t - dec.openAt) / A.floodS));

  const chests: ArrivalChestFrame[] = slots.map((slot, i) => {
    const pose = chestPose(plan, t, i, slot, dec);
    const fa = fallAt(i);
    const la = landAt(i);
    const beamU = clamp01((t - beamAt(i)) / A.beamDrop);
    const beamFoot = easeOutCubic(beamU);
    // The beam: on as it drops, flaring as the chest lands, then a standing column.
    const flare = t >= la ? Math.exp(-(t - la) * 5) : 0;
    let beamK = t < beamAt(i) ? 0 : Math.min(1, beamU * 1.6) * (t < la ? 1 : 0.42 + 0.58 * flare);
    if (idle) beamK *= 0.9 + 0.1 * Math.sin(t * 2.3 + i * 1.9);
    let pedestal = clamp01((t - beamAt(i) - A.beamDrop * 0.6) / 0.25) * 0.55;
    if (t >= la) pedestal = 0.7 + 0.3 * Math.exp(-(t - la) * 4);
    const dust = t >= la && t < la + A.dustS ? (t - la) / A.dustS : -1;
    const press = t >= la ? Math.max(0, spring(t - la, 22, 9)) : 0;
    const shock = t >= la && t < la + A.shockS ? (t - la) / A.shockS : -1;
    // Seam light: on from the landing, breathing, flaring on a rattle.
    let seam = t < la ? 0 : clamp01((t - la) / 0.5) * (0.55 + 0.2 * Math.sin(t * 3.1 + i));
    seam += 1.1 * rattleK(plan, t, i, dec);
    let alpha = 1;
    let visible = t >= fa;
    let flight: ArrivalChestFrame['flight'] = null;
    if (dec.openAt !== null && i > 0) {
      // TAP TO OPEN: the others step aside (they come back one by one, each with its own reveal).
      const e = clamp01((t - dec.openAt) / A.exitS);
      alpha = 1 - e;
      if (e >= 1) visible = false;
    }
    if (dec.openAt !== null) {
      beamK *= 1 - clamp01((t - dec.openAt) / 0.4);
      // The first chest keeps its pedestal into the reveal (the same object, standing where it was).
      if (i > 0) pedestal *= 1 - flood;
      seam *= 1 - clamp01((t - dec.openAt) / 0.2);
    }
    if (dec.laterAt !== null) {
      const fs = flightStart(dec.laterAt, i);
      const off = clamp01((t - stowStart(dec.laterAt, i)) / 0.3);
      beamK *= 1 - off;
      pedestal *= 1 - off;
      if (t >= fs) {
        // The canvas chest hands itself to the DOM flyer (over the top bar) on this frame.
        visible = false;
        const u = (t - fs) / A.stowFlight;
        if (u < 1 && !plan.reduced) {
          const head = flightPoint(slot, view, u);
          // The streak's tail: where the head was 0.12 of the flight ago.
          const tail = flightPoint(slot, view, Math.max(0, u - 0.12));
          const stretch = 1 + 0.12 * (1 - easeOutCubic(clamp01(u / 0.22)));
          flight = {
            u,
            x: head.x,
            y: head.y,
            size: head.size,
            stretch,
            tail: { x: tail.x, y: tail.y, size: tail.size },
            glow: clamp01(u * 8),
          };
        }
      }
      if (plan.reduced) {
        alpha = 1 - clamp01((t - dec.laterAt) / 0.3);
        visible = alpha > 0;
      }
    }
    return {
      visible,
      alpha,
      pose,
      slot,
      pedestal,
      beamFoot,
      beamK,
      press,
      landed: t >= la ? clamp01((t - la) / 0.2) : 0,
      dust,
      shock,
      seam: Math.max(0, seam),
      flight,
    };
  });

  let dock = 0;
  let layer = 1;
  if (dec.laterAt !== null) {
    const last = stowLand(dec.laterAt, n - 1);
    dock = easeOutCubic(clamp01((t - dec.laterAt) / 0.3));
    const end = last + A.stowHold;
    const gone = clamp01((t - end) / A.dockOut);
    dock *= 1 - gone;
    layer = 1 - gone;
  }
  if (plan.reduced && dec.laterAt === null && dec.openAt === null)
    layer = clamp01((t - reducedStart(plan)) / 0.3);
  const end = doneAt(plan, dec);
  return {
    t,
    veil,
    flood,
    title: clamp01((t - titleAt()) / A.slamS),
    titleOut: out,
    count: n > 1 || plan.count > 1 ? clamp01((t - countAt(plan)) / 0.28) : 0,
    countOut: out,
    buttons: clamp01((t - buttonsAt(plan)) / A.buttonsS),
    buttonsOut: out,
    live,
    idle,
    chests,
    dock,
    layer,
    done: end !== null && t >= end,
  };
}

/** The rattle on chest i at t: its strength 0..1 (a sine hump) and seconds into it, or null. */
export function rattleOn(
  plan: ArrivalPlan,
  t: number,
  i: number,
  dec: ArrivalDecision,
): { k: number; since: number } | null {
  if (plan.reduced) return null;
  const decidedAt = dec.openAt ?? dec.laterAt;
  const first = rattleAt(plan, 0);
  if (t < first) return null;
  const n = Math.floor((t - first) / A.rattleEvery);
  const at = rattleAt(plan, n);
  if (rattleChest(plan, n) !== i) return null;
  if (decidedAt !== null && at >= decidedAt) return null;
  const since = t - at;
  if (since > A.rattleS) return null;
  return { k: Math.sin((Math.PI * since) / A.rattleS), since };
}

/** 0..1 through a lid rattle on chest i at t (0 when none). */
export function rattleK(plan: ArrivalPlan, t: number, i: number, dec: ArrivalDecision): number {
  return rattleOn(plan, t, i, dec)?.k ?? 0;
}

/** Chest i's pose at t (before any hand-over: the stage blends the reveal in from this). */
export function chestPose(
  plan: ArrivalPlan,
  t: number,
  i: number,
  slot: ChestLayout,
  dec: ArrivalDecision,
): ChestPose {
  const fa = fallAt(i);
  const la = landAt(i);
  const pose: ChestPose = {
    x: slot.x,
    y: slot.y,
    size: slot.size,
    yaw: idleYaw(t),
    pitch: BASE_PITCH,
    roll: 0,
    sx: 1,
    sy: 1,
    lift: 0,
    lid: 0,
    flash: 0,
    rim: BASE_RIM,
    emit: 0,
  };
  if (t < la) {
    // The fall: from above the top edge, down the beam, gathering speed; a turn and a stretch with the speed.
    const u = clamp01((t - fa) / A.fallS);
    const e = easeInCubic(u);
    const top = (slot.y + slot.size) / slot.size; // chest units from the slot to above the screen
    pose.lift = (1 - e) * top;
    pose.yaw = BASE_YAW - 0.9 * (1 - e);
    pose.roll = 0.1 * (1 - e) * (i % 2 === 0 ? 1 : -1);
    const stretch = 0.14 * u * u;
    pose.sy = 1 + stretch;
    pose.sx = 1 - stretch * 0.5;
    pose.rim = BASE_RIM + 0.6 * (1 - u);
    return pose;
  }
  // The slam: a heavy squash that springs back through a stretch, a white kiss of light on contact.
  const since = t - la;
  const s = spring(since, 15, 6.2);
  pose.sy = 1 - 0.26 * s;
  pose.sx = 1 + 0.15 * s;
  pose.flash = 0.5 * Math.exp(-since * 14);
  // The rebound: one small hop off the pedestal as the squash springs back, then it sits.
  const hop = (since - 0.1) / 0.24;
  if (hop > 0 && hop < 1) pose.lift += 0.075 * Math.sin(Math.PI * hop);
  pose.rim = BASE_RIM + 0.5 * Math.exp(-since * 5);
  // Alive: the seam's light in the body, a breathing bob, the lid cracked on its light.
  const inK = clamp01((since - 0.25) / 0.5);
  const breathe = Math.sin((t - la) * ((2 * Math.PI) / 2.3) + i * 1.3);
  const still = plan.reduced ? 0 : 1;
  pose.lift = 0.028 * inK * (0.5 + 0.5 * breathe) * still;
  pose.sx += 0.012 * inK * breathe * still;
  pose.sy -= 0.012 * inK * breathe * still;
  pose.lid = inK * (0.07 + 0.015 * Math.sin(t * 3.1 + i) * still);
  pose.emit = inK * (0.55 + 0.15 * Math.sin(t * 2.2 + i));
  const rattle = rattleOn(plan, t, i, dec);
  if (rattle) {
    const r = rattle.k;
    // The lid knocks three or four times on its hinge; the body rocks a hair with it.
    const jitter = Math.sin(rattle.since * 62 + h01(i) * 6) * r;
    pose.lid += 0.16 * r * (0.6 + 0.4 * Math.abs(jitter));
    pose.roll += 0.035 * jitter;
    pose.lift += 0.02 * r;
    pose.emit += 0.9 * r;
  }
  // LATER: a wind-up (a squash, then it springs up) before the flight takes it.
  if (dec.laterAt !== null) {
    const w = (t - stowStart(dec.laterAt, i)) / A.stowWind;
    if (w >= 0) {
      const k = clamp01(w);
      const squash = Math.sin(Math.PI * Math.min(1, k * 1.6)) * (k < 0.62 ? 1 : 0);
      pose.sy = 1 - 0.14 * squash + (k > 0.62 ? 0.12 * easeOutBack((k - 0.62) / 0.38) : 0);
      pose.sx = 1 + 0.08 * squash - (k > 0.62 ? 0.05 : 0);
      pose.lift = Math.max(pose.lift, k > 0.62 ? (0.1 * (k - 0.62)) / 0.38 : 0);
      // Into the baked still's pose, so the flyer takes over on an identical frame.
      const e = easeOutCubic(k);
      pose.yaw += (BASE_YAW - pose.yaw) * e;
      pose.roll *= 1 - e;
      pose.lid += (STILL_LID - pose.lid) * e;
      pose.emit += (STILL_EMIT - pose.emit) * e;
      pose.rim += (STILL_RIM - pose.rim) * e;
    }
  }
  return pose;
}

/* --------------------------------------------------------------------------
   The clock
   -------------------------------------------------------------------------- */

export type ArrivalEvent =
  | { type: 'beam'; index: number }
  | { type: 'land'; index: number }
  | { type: 'rattle'; index: number }
  | { type: 'stow'; index: number }
  | { type: 'done' };

/**
 * The arrival's clock: its time, the two decisions and the events the time
 * has passed (for the sound and the pill). `t` only moves through `step`.
 */
export class ArrivalClock {
  t: number;
  readonly dec: ArrivalDecision = { openAt: null, laterAt: null };
  private readonly queue: ArrivalEvent[] = [];
  private beams = 0;
  private lands = 0;
  private rattles = 0;
  private stows = 0;
  private finished = false;

  constructor(readonly plan: ArrivalPlan) {
    // Reduced motion: straight to the end state (the layer fades in); no fall, no rings.
    this.t = plan.reduced ? reducedStart(plan) : 0;
    if (plan.reduced) {
      this.beams = shown(plan);
      this.lands = shown(plan);
    }
  }

  /** TAP TO OPEN (or a tap on the chest). Returns whether it was taken. */
  open(): boolean {
    if (!this.live) return false;
    this.dec.openAt = this.t;
    return true;
  }

  /** LATER. Returns whether it was taken. */
  later(): boolean {
    if (!this.live) return false;
    this.dec.laterAt = this.t;
    return true;
  }

  /** The buttons are up and nothing has been decided. */
  get live(): boolean {
    return this.dec.openAt === null && this.dec.laterAt === null && this.t >= buttonsAt(this.plan);
  }

  step(dt: number): void {
    this.t += Math.max(0, dt);
    const n = shown(this.plan);
    const t = this.t;
    while (this.beams < n && t >= beamAt(this.beams))
      this.queue.push({ type: 'beam', index: this.beams++ });
    while (this.lands < n && t >= landAt(this.lands))
      this.queue.push({ type: 'land', index: this.lands++ });
    const decidedAt = this.dec.openAt ?? this.dec.laterAt;
    if (!this.plan.reduced)
      while (
        t >= rattleAt(this.plan, this.rattles) &&
        (decidedAt === null || rattleAt(this.plan, this.rattles) < decidedAt)
      )
        this.queue.push({ type: 'rattle', index: rattleChest(this.plan, this.rattles++) });
    if (this.dec.laterAt !== null) {
      while (this.stows < n && t >= stowLand(this.dec.laterAt, this.stows))
        this.queue.push({ type: 'stow', index: this.stows++ });
      const end = doneAt(this.plan, this.dec);
      if (!this.finished && end !== null && t >= end) {
        this.finished = true;
        this.queue.push({ type: 'done' });
      }
    }
  }

  /** Events since the last drain, in order. */
  drain(): ArrivalEvent[] {
    return this.queue.splice(0, this.queue.length);
  }

  frame(view: ArrivalView): ArrivalFrame {
    return arrivalFrame(this.plan, this.t, this.dec, view);
  }
}
